/**
 * PlanLadder — the collapsed "Plans & pricing" section. Expanding it shows
 * what the current plan includes and a compare grid of every plan with a
 * switch action. It reads the shared PLAN_CATALOG so it never drifts from the
 * entitlement limits.
 *
 * It's collapsed by default: the hero already tells the founder which plan
 * they're on, so the full ladder is opt-in detail, not front-and-centre.
 *
 * Plan switching is real and in-app (`billing.changePlan`): the new limits
 * apply immediately. It's Owner-only (`billing.manage`) and available ONLY to
 * an *active* (paying) subscription. While on a trial — or any non-active
 * state — the customer picks their plan via the sales-led "Talk to us to
 * activate" flow, so every CTA falls back to that link. Money movement stays
 * sales-led; this is an entitlement change, not a checkout.
 * @see system-decisions §15.
 */
import * as React from "react";
import { Zap, Check, ArrowUp, ArrowDown, Users, HardDrive, MessageSquare, Loader2 } from "lucide-react";
import { cn, ConfirmDialog, StatusPill, toast } from "@flint/ui";
import {
  PLAN_BY_CODE,
  PLAN_CATALOG,
  formatPlanPrice,
  isSelfServePlan,
  type PlanCatalogEntry,
  type PlanCode,
} from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { FormCard } from "../form-card.tsx";
import { TALK_URL } from "./billing-hero.tsx";
import { useSubscriptionCheckout } from "./razorpay.ts";

const BADGE: Record<string, string> = {
  popular: "bg-sky-50 text-sky-700 ring-sky-100",
  exclusive: "bg-amber-50 text-amber-700 ring-amber-100",
};

function PlanBadge({ entry }: { entry: PlanCatalogEntry }) {
  if (!entry.badge || !entry.badgeTone) return null;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1", BADGE[entry.badgeTone])}>
      {entry.badge}
    </span>
  );
}

function planLimits(p: PlanCatalogEntry) {
  return [
    { icon: Users, text: `${p.seats} team members` },
    { icon: HardDrive, text: `${p.storageGb} GB storage` },
    { icon: MessageSquare, text: `${p.waCreditsPerMonth.toLocaleString("en-IN")} WhatsApp credits / mo` },
  ];
}

/** Power-rank by position in the ordered catalogue → upgrade vs downgrade. */
function rankOf(code: string): number {
  return PLAN_CATALOG.findIndex((p) => p.code === code);
}
type Direction = "upgrade" | "downgrade" | "lateral";
function directionOf(from: string, to: string): Direction {
  const a = rankOf(from);
  const b = rankOf(to);
  if (b > a) return "upgrade";
  if (b < a) return "downgrade";
  return "lateral";
}

export interface PlanLadderProps {
  plan: string;
  /** Subscription status — self-serve switching is for `active` only; others get "Talk to us". */
  status: string;
}

export function PlanLadder({ plan, status }: PlanLadderProps) {
  const current = PLAN_BY_CODE[plan] ?? PLAN_CATALOG[0]!;
  const [target, setTarget] = React.useState<PlanCatalogEntry | null>(null);

  const canManage = usePermission("billing.manage");
  const isActive = status === "active";
  /**
   * Owner on an ACTIVE subscription → switch plan in-app (`changePlan`, which
   * also syncs the Razorpay subscription). Owner on a NON-active subscription →
   * activate a self-serve plan (Razorpay subscription checkout). Everyone else,
   * and any non-self-serve tier (Design Partner), falls back to "Talk to us".
   */
  const canSwitch = canManage && isActive;
  const { activate, pendingPlan } = useSubscriptionCheckout();

  const utils = trpc.useUtils();
  const changePlan = trpc.billing.changePlan.useMutation({
    onSuccess: (res) => {
      const name = PLAN_BY_CODE[res.plan]?.name ?? res.plan;
      toast.success(`You're now on the ${name} plan`, {
        description: "Your new limits apply right away.",
      });
      void utils.billing.overview.invalidate();
      void utils.identity.me.invalidate(); // entitlements (modules / writeEnabled) drive nav
      setTarget(null);
    },
    onError: (err) => {
      toast.error("Couldn't change plan", { description: err.message });
    },
  });

  const targetDirection: Direction | null = target ? directionOf(plan, target.code) : null;
  const verb =
    targetDirection === "upgrade" ? "Upgrade" : targetDirection === "downgrade" ? "Downgrade" : "Switch";

  return (
    <FormCard
      icon={<Zap size={14} strokeWidth={2} aria-hidden />}
      title="Plans & pricing"
      description="Compare what each plan includes and switch anytime."
      badge={
        <StatusPill tone="muted" size="xs">
          {current.name}
        </StatusPill>
      }
      collapsible
    >
      {/* What's included in the current plan */}
      <p className="text-xs font-semibold uppercase tracking-wider text-muted">
        What's included in {current.name}
      </p>
      <ul className="mt-2 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {current.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-ink-2">
            <Check size={15} className="mt-0.5 shrink-0 text-ok" aria-hidden />
            {f}
          </li>
        ))}
      </ul>

      <div className="my-5 border-t border-line" />

      {/* Compare all plans */}
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted">All plans</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {PLAN_CATALOG.map((p) => {
          const isCurrent = p.code === plan;
          const dir = directionOf(plan, p.code);
          return (
            <div
              key={p.code}
              className={cn(
                "flex flex-col rounded-md border bg-paper p-4 shadow-card",
                isCurrent ? "border-sky-600 ring-1 ring-sky-100" : "border-line",
              )}
            >
              <div className="mb-1 flex h-5 items-center">
                {isCurrent ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700 ring-1 ring-sky-100">
                    <Check size={11} aria-hidden /> Current plan
                  </span>
                ) : (
                  <PlanBadge entry={p} />
                )}
              </div>
              <p className="text-sm font-bold text-ink">{p.name}</p>
              <p className="mt-0.5 text-xs text-muted">{p.tagline}</p>
              <p className="mt-1.5 text-sm font-semibold text-ink">
                {formatPlanPrice(p) ?? "Custom pricing"}
              </p>
              <div className="my-3 border-t border-line" />
              <ul className="flex flex-col gap-1.5">
                {planLimits(p).map((l) => (
                  <li key={l.text} className="flex items-center gap-1.5 text-xs text-ink-2">
                    <l.icon size={13} className="text-muted" aria-hidden /> {l.text}
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <span className="mt-4 inline-flex h-8 items-center justify-center rounded-sm border border-line bg-bg text-xs font-semibold text-muted">
                  Your plan
                </span>
              ) : canSwitch && isSelfServePlan(p) ? (
                <button
                  type="button"
                  onClick={() => setTarget(p)}
                  className="mt-4 inline-flex h-8 items-center justify-center gap-1 rounded-sm border border-line bg-paper text-xs font-semibold text-ink hover:bg-bg"
                >
                  {dir === "upgrade" ? (
                    <>
                      <ArrowUp size={12} aria-hidden /> Upgrade
                    </>
                  ) : (
                    <>
                      <ArrowDown size={12} aria-hidden /> Downgrade
                    </>
                  )}
                </button>
              ) : !isActive && canManage && isSelfServePlan(p) ? (
                <button
                  type="button"
                  disabled={pendingPlan === p.code}
                  onClick={() => void activate(p.code as PlanCode)}
                  className="mt-4 inline-flex h-8 items-center justify-center gap-1 rounded-sm bg-ink text-xs font-semibold text-paper hover:bg-ink-2 disabled:opacity-60"
                >
                  {pendingPlan === p.code ? (
                    <>
                      <Loader2 size={12} className="animate-spin" aria-hidden /> Starting…
                    </>
                  ) : (
                    "Activate"
                  )}
                </button>
              ) : (
                <a
                  href={TALK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex h-8 items-center justify-center rounded-sm border border-line bg-paper text-xs font-semibold text-ink hover:bg-bg"
                >
                  Talk to us
                </a>
              )}
            </div>
          );
        })}
      </div>

      {/* Confirm plan change */}
      <ConfirmDialog
        open={target !== null}
        onOpenChange={(o) => {
          if (!o) setTarget(null);
        }}
        intent={targetDirection === "downgrade" ? "danger" : "default"}
        title={target ? `${verb} to ${target.name}?` : ""}
        confirmLabel={target ? `${verb} now` : "Confirm"}
        pending={changePlan.isPending}
        onConfirm={async () => {
          if (!target) return;
          await changePlan.mutateAsync({ toPlan: target.code });
        }}
        description={
          target ? (
            <span className="block">
              Your plan changes immediately and the new limits apply right away — no checkout.
              <span className="mt-2 block font-medium text-ink">
                {target.name}: {target.seats} members · {target.storageGb} GB · {target.waCreditsPerMonth.toLocaleString("en-IN")} WhatsApp credits/mo
              </span>
              {targetDirection === "downgrade" ? (
                <span className="mt-2 block text-xs text-muted">
                  If your current team size or storage is above the {target.name} limit, the change is blocked until you reduce usage.
                </span>
              ) : null}
            </span>
          ) : (
            ""
          )
        }
      />
    </FormCard>
  );
}
